// src/pages/lab/processing/processingtabs/components/stepExecution/ResultProcessingView.tsx
//
// The working surface for a results step: upload result files (or enter data),
// then review what has been uploaded for the group.

import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Input, InputNumber, Progress, Select, Tabs, Typography, Upload } from "antd";
import {
	CheckCircleOutlined,
	CloudUploadOutlined,
	DeleteOutlined,
	FileTextOutlined,
	PlayCircleOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload";
import type { ColumnsType } from "antd/es/table";

import { DataTable, EmptyState, StatusTag } from "../../../../../../components/shared";
import UploadedResultSummary from "./UploadedResultSummary";

// API
import { analyzeResult, fetchGroupResults } from "../../../../../../api/labiq/SampleResultApi";

// Types
import type {
	GroupStepInstance,
	ProcessingGroup,
	ProtocolStep,
	StepInstanceData,
} from "../../../../../../types/lab/ProtocolTypes";

import type { SampleResult, UploadResultRequest } from "../../../../../../types/lab/SampleResultTypes";

// Hooks
import { useNotifications } from "../../../../../../context/NotificationContext";
import { useFileUpload } from "../../../../../../hooks/useFileUpload";
import { getSessionUser } from "../../../../../../utils/auth/sessionUser";

// Utils
import {
	calculateTotalFileSize,
	formatFileSize,
	formatFileSizeFromMB,
	validateFileExtension,
} from "../../../../../../utils/fileUtils";
import { validateGsaRawBundle } from "../../../../../../utils/genotyper";
import { requireLabiqUserId } from "../../../../../../utils/auth/labiqUserId";

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface ResultProcessingViewProps {
	group: ProcessingGroup;
	step: ProtocolStep;
	stepData?: StepInstanceData | GroupStepInstance;
	// The panel's live step status; "completed" closes the upload form and opens the summary.
	stepStatus?: string;
	onRefresh: (groupData?: ProcessingGroup, refreshParent?: boolean) => void;
	// labiq's group upload does not check group status, so a non-active group must not offer the form.
	readOnly?: boolean;
}

const UPLOAD_TAB = "1";
const RESULTS_TAB = "2";
const PROCESSING_POLL_INTERVAL_MS = 30_000;

// Newest first by upload time, falling back to id for equal timestamps.
const newestResult = (list: SampleResult[]): SampleResult | undefined =>
	[...list].sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0) || b.id - a.id)[0];

interface ResultConfiguration {
	result_types: string[];
	auto_analyze: boolean;
	requires_verification: boolean;
	file_restrictions?: {
		allowed_extensions: string[];
		max_file_size: number;
		min_files?: number;
		max_files?: number;
	};
	custom_fields?: Array<{
		name: string;
		type: string;
		required: boolean;
		options?: string[];
	}>;
}

const TAB_BODY: React.CSSProperties = { padding: "12px 16px" };

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div style={{ fontSize: 10, color: "var(--mg-text-secondary)", marginBottom: 4 }}>{children}</div>
);

const ResultProcessingView: React.FC<ResultProcessingViewProps> = ({ group, step, stepStatus, onRefresh, readOnly = false }) => {
	const user = getSessionUser();
	const isCompleted = stepStatus === "completed";
	const uploadClosed = isCompleted || readOnly;

	const getResultConfig = (): ResultConfiguration => {
		const config = step.configuration as any;
		return (
			config?.result_configuration || {
				result_types: ["file_upload"],
				auto_analyze: true,
				requires_verification: true,
				file_restrictions: {
					allowed_extensions: [],
					max_file_size: 10000000000,
					min_files: 1,
					max_files: 10,
				},
			}
		);
	};

	const resultConfig = getResultConfig();
	const isAnalysisProcessing = !!group.analysis_instances && group.analysis_instances.length > 0;
	const processingTypeLabel = isAnalysisProcessing ? "analyses" : "samples";

	const [activeTab, setActiveTab] = useState(uploadClosed ? RESULTS_TAB : UPLOAD_TAB);
	const [analyzing, setAnalyzing] = useState(false);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [notes, setNotes] = useState("");
	const [resultType] = useState<string>(resultConfig.result_types[0] || "file_upload");
	const [manualData, setManualData] = useState<Record<string, any>>({});
	const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
	const [results, setResults] = useState<SampleResult[]>([]);
	const [expandedResultIds, setExpandedResultIds] = useState<React.Key[]>([]);
	// The newest row opens once on first load, not again after the user collapses it and a poll lands.
	const defaultExpansionApplied = useRef(false);

	// The AGRF bundle is one zip and the run panel replaces file restrictions for it
	const isGsaRaw = resultType === "gsa_raw";

	const { showSuccess, showError } = useNotifications();

	const {
		uploading,
		progress: uploadProgress,
		error: uploadError,
		uploadGroup,
		reset: resetUpload,
	} = useFileUpload({
		onSuccess: (response) => {
			if (isGsaRaw) {
				showSuccess("Bundle uploaded — the run will appear below and you will be notified when it finishes");
			} else {
				showSuccess(`Result uploaded — you will be notified when analysis completes`);
			}
			if (response.result?.id) setExpandedResultIds([response.result.id]);
			resetForm();
			loadExistingResults();
			setActiveTab(RESULTS_TAB);
			onRefresh(group, false);
		},
		onError: (error) => {
			showError(`Failed to upload result: ${error.message}`);
		},
	});

	useEffect(() => {
		loadExistingResults();
		initializeCustomFields();
	}, []);

	// "Complete step" flips the status while this view stays mounted, so the tab follows it.
	useEffect(() => {
		if (uploadClosed) setActiveTab(RESULTS_TAB);
	}, [uploadClosed]);

	const anyProcessing = results.some((result) => result.status === "processing");

	useEffect(() => {
		if (!anyProcessing) return;
		const interval = setInterval(loadExistingResults, PROCESSING_POLL_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [anyProcessing]);

	const resetForm = () => {
		setFileList([]);
		setNotes("");
		setManualData({});
		setCustomFieldValues({});
		initializeCustomFields();
		resetUpload();
	};

	const loadExistingResults = async () => {
		try {
			const resultsList = await fetchGroupResults(group.id);
			setResults(resultsList);
			const newest = newestResult(resultsList);
			if (newest && !defaultExpansionApplied.current) {
				defaultExpansionApplied.current = true;
				setExpandedResultIds((prev) => (prev.length > 0 ? prev : [newest.id]));
			}
		} catch (error) {
			console.warn("Could not load existing results:", error);
		}
	};

	const initializeCustomFields = () => {
		const initialValues: Record<string, any> = {};

		if (resultConfig.custom_fields) {
			resultConfig.custom_fields.forEach((field) => {
				initialValues[field.name] = "";
			});
		}

		setCustomFieldValues(initialValues);
	};

	const validateUpload = (): string | null => {
		// Exactly one zip, and labiq's file_restrictions do not apply to the bundle
		if (isGsaRaw) {
			return validateGsaRawBundle(fileList.map((f) => f.originFileObj).filter((file): file is RcFile => !!file));
		}

		if (resultType === "file_upload" && fileList.length === 0) {
			return "Select at least one file to upload";
		}

		if (resultConfig.file_restrictions) {
			const {
				min_files = 1,
				max_files = 10,
				allowed_extensions = [],
			} = resultConfig.file_restrictions;

			if (fileList.length < min_files) {
				return `Select at least ${min_files} file${min_files !== 1 ? "s" : ""}`;
			}

			if (fileList.length > max_files) {
				return `Select no more than ${max_files} files`;
			}

			for (const file of fileList) {
				if (file.originFileObj) {
					const extensionValidation = validateFileExtension(file.originFileObj, allowed_extensions);
					if (!extensionValidation.isValid) {
						return extensionValidation.message!;
					}
				}
			}
		}

		if (resultConfig.custom_fields) {
			for (const field of resultConfig.custom_fields) {
				if (field.required && (!customFieldValues[field.name] || customFieldValues[field.name] === "")) {
					return `"${field.name.replace(/_/g, " ")}" is required`;
				}
			}
		}

		return null;
	};

	const handleUploadResult = async () => {
		const validationError = validateUpload();
		if (validationError) {
			showError(validationError);
			return;
		}

		const selectedFiles = fileList
			.map((f) => f.originFileObj)
			.filter((file): file is RcFile => file !== undefined);

		if (resultType === "file_upload" && selectedFiles.length === 0) {
			showError("No valid files selected for upload");
			return;
		}

		const uploadRequest: UploadResultRequest = {
			result_type: resultType as any,
			uploaded_by: requireLabiqUserId(user),
			notes: notes.trim() || undefined,
			auto_analyze: resultConfig.auto_analyze,
			files: selectedFiles,
		};

		const combinedData = { ...manualData, ...customFieldValues };
		if (Object.keys(combinedData).length > 0) {
			uploadRequest.data = combinedData;
		}

		await uploadGroup(group.id, uploadRequest);
	};

	const handleAnalyzeResult = async (result: SampleResult) => {
		setAnalyzing(true);
		try {
			await analyzeResult(result.id);
			showSuccess("Result analyzed");
			await loadExistingResults();
		} catch (error) {
			showError("Failed to analyze result");
		} finally {
			setAnalyzing(false);
		}
	};

	const renderCustomFieldInput = (field: any): JSX.Element => {
		const value = customFieldValues[field.name] || "";

		switch (field.type) {
			case "number":
				return (
					<InputNumber
						value={value || null}
						onChange={(val) => setCustomFieldValues((prev) => ({ ...prev, [field.name]: val }))}
						style={{ width: "100%" }}
						size="small"
					/>
				);
			case "dropdown":
				return (
					<Select
						value={value}
						onChange={(val) => setCustomFieldValues((prev) => ({ ...prev, [field.name]: val }))}
						style={{ width: "100%" }}
						placeholder={`Select ${field.name.replace(/_/g, " ")}`}
						size="small"
					>
						{(field.options || []).map((opt: string) => (
							<Option key={opt} value={opt}>
								{opt}
							</Option>
						))}
					</Select>
				);
			case "boolean":
				return (
					<Checkbox
						checked={value || false}
						onChange={(e) => setCustomFieldValues((prev) => ({ ...prev, [field.name]: e.target.checked }))}
					>
						<Text style={{ fontSize: 12 }}>{field.name.replace(/_/g, " ")}</Text>
					</Checkbox>
				);
			default:
				return (
					<Input
						value={value}
						onChange={(e) => setCustomFieldValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
						size="small"
					/>
				);
		}
	};

	const uploadProps: UploadProps = {
		multiple: !isGsaRaw,
		fileList,
		beforeUpload: (file) => {
			if (isGsaRaw) {
				const bundleError = validateGsaRawBundle([...fileList.map((f) => f.originFileObj).filter((existing): existing is RcFile => !!existing), file]);
				if (bundleError) {
					showError(bundleError);
					return Upload.LIST_IGNORE;
				}
			}
			const uploadFile: UploadFile = {
				uid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
				name: file.name,
				size: file.size,
				type: file.type,
				status: "done",
				originFileObj: file,
			};
			setFileList((prev) => [...prev, uploadFile]);
			return false;
		},
		onRemove: (file) => {
			setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
		},
		accept: isGsaRaw ? ".zip" : resultConfig.file_restrictions?.allowed_extensions?.map((ext) => `.${ext}`).join(","),
		maxCount: isGsaRaw ? 1 : resultConfig.file_restrictions?.max_files || 10,
		showUploadList: false,
	};

	const resultColumns: ColumnsType<SampleResult> = [
		{
			title: "Type",
			dataIndex: "result_type",
			key: "result_type",
			width: 110,
			render: (type: string) => (
				<Text style={{ fontSize: 12 }}>{(type ?? "").replace(/_/g, " ")}</Text>
			),
		},
		{
			title: "Status",
			key: "status",
			width: 170,
			render: (_: any, record: SampleResult) => (
				<span style={{ display: "inline-flex", gap: 4 }}>
					<StatusTag status={record.status || "pending"} size="small" />
					{record.verification_status !== "pending" && (
						<StatusTag status={record.verification_status} size="small" />
					)}
				</span>
			),
		},
		{
			title: "Uploaded",
			dataIndex: "created_at",
			key: "created_at",
			width: 110,
			render: (date: string) => (
				<Text style={{ fontSize: 12 }}>{date ? new Date(date).toLocaleDateString() : "—"}</Text>
			),
		},
		{
			title: "Notes",
			dataIndex: "notes",
			key: "notes",
			render: (value: string) => (
				<Text type="secondary" style={{ fontSize: 11 }}>
					{value || "—"}
				</Text>
			),
		},
		{
			title: "",
			key: "actions",
			width: 72,
			render: (_: any, record: SampleResult) => (
				<span style={{ display: "inline-flex", gap: 2 }}>
					{record.status !== "completed" && resultConfig.auto_analyze && record.result_type !== "gsa_raw" && (
						<Button
							type="text"
							size="small"
							icon={<PlayCircleOutlined />}
							onClick={() => handleAnalyzeResult(record)}
							loading={analyzing}
						/>
					)}
				</span>
			),
		},
	];

	const totalSelectedSize = formatFileSize(
		calculateTotalFileSize(fileList.map((f) => f.originFileObj as File).filter(Boolean)),
	);

	const renderUploadTab = () => (
		<div style={{ ...TAB_BODY, display: "flex", flexDirection: "column", gap: 16 }}>
			{resultType !== "manual_entry" && (
				<div>
					<Upload.Dragger {...uploadProps}>
						<div style={{ padding: "12px 0" }}>
							<CloudUploadOutlined style={{ fontSize: 28, color: "var(--mg-primary)" }} />
							<div style={{ fontSize: 12, fontWeight: 500, marginTop: 8, color: "var(--mg-text)" }}>
								{isGsaRaw ? "Drop the AGRF zip here, or click to browse" : "Drop result files here, or click to browse"}
							</div>
							{isGsaRaw && (
								<Text type="secondary" style={{ fontSize: 11 }}>
									Zip the AGRF delivery folder as received (sample sheet, IDATs, FinalReports). One zip per upload.
								</Text>
							)}
							{!isGsaRaw && resultConfig.file_restrictions && (
								<Text type="secondary" style={{ fontSize: 11 }}>
									Max {formatFileSizeFromMB(resultConfig.file_restrictions.max_file_size || 10240)}{" "}
									per file
									{resultConfig.file_restrictions.allowed_extensions?.length > 0 &&
										` · ${resultConfig.file_restrictions.allowed_extensions.join(", ")}`}
								</Text>
							)}
						</div>
					</Upload.Dragger>

					{fileList.length > 0 && (
						<div
							style={{
								marginTop: 12,
								border: "1px solid var(--mg-border)",
								borderRadius: "var(--mg-radius-sm)",
							}}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									padding: "6px 10px",
									borderBottom: "1px solid var(--mg-border)",
									background: "var(--mg-bg-subtle)",
								}}
							>
								<Text style={{ fontSize: 11 }}>
									{fileList.length} file{fileList.length !== 1 ? "s" : ""} · {totalSelectedSize}
								</Text>
								<Button type="text" size="small" onClick={() => setFileList([])}>
									Clear all
								</Button>
							</div>
							{fileList.map((file) => (
								<div
									key={file.uid}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: 8,
										padding: "4px 10px",
										borderBottom: "1px solid var(--mg-border)",
									}}
								>
									<span
										style={{
											display: "flex",
											alignItems: "center",
											gap: 8,
											minWidth: 0,
											fontSize: 12,
										}}
									>
										<FileTextOutlined style={{ color: "var(--mg-text-secondary)" }} />
										<span
											style={{
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{file.name}
										</span>
										<Text type="secondary" style={{ fontSize: 10, flexShrink: 0 }}>
											{file.originFileObj
												? formatFileSize(file.originFileObj.size)
												: file.size
												? formatFileSize(file.size)
												: ""}
										</Text>
									</span>
									<Button
										type="text"
										size="small"
										icon={<DeleteOutlined />}
										onClick={() => setFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
									/>
								</div>
							))}
						</div>
					)}
				</div>
			)}

			{resultType === "manual_entry" && (
				<div>
					<FieldLabel>Result data (JSON)</FieldLabel>
					<TextArea
						value={JSON.stringify(manualData, null, 2)}
						onChange={(e) => {
							try {
								setManualData(JSON.parse(e.target.value || "{}"));
							} catch (error) {
								// Invalid JSON — keep the last parsable value
							}
						}}
						placeholder='{"key": "value"}'
						rows={6}
						style={{ fontFamily: "monospace" }}
						size="small"
					/>
				</div>
			)}

			{resultConfig.custom_fields && resultConfig.custom_fields.length > 0 && (
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
						gap: 12,
					}}
				>
					{resultConfig.custom_fields.map((field, index) => (
						<div key={index}>
							<FieldLabel>
								{field.name.replace(/_/g, " ")}
								{field.required && <Text type="danger"> *</Text>}
							</FieldLabel>
							{renderCustomFieldInput(field)}
						</div>
					))}
				</div>
			)}

			<div>
				<FieldLabel>Notes</FieldLabel>
				<TextArea
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					placeholder="Anything worth recording about this result"
					rows={2}
					size="small"
				/>
			</div>

			{uploading && (
				<div>
					<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
						<Text style={{ fontSize: 11 }}>
							Uploading {fileList.length} file{fileList.length !== 1 ? "s" : ""} · {totalSelectedSize}
						</Text>
						<Text type="secondary" style={{ fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
							{uploadProgress}%
						</Text>
					</div>
					<Progress percent={uploadProgress} strokeWidth={6} showInfo={false} />
				</div>
			)}

			{uploadError && !uploading && (
				<Alert
					type="error"
					message={`Upload failed: ${uploadError}`}
					action={
						<Button size="small" onClick={handleUploadResult}>
							Retry
						</Button>
					}
					closable
				/>
			)}

			<div style={{ display: "flex", justifyContent: "flex-end" }}>
				<Button
					type="primary"
					size="small"
					icon={<UploadOutlined />}
					onClick={handleUploadResult}
					loading={uploading}
					disabled={(resultType === "file_upload" || isGsaRaw) && fileList.length === 0}
				>
					Upload result for {group.sample_count || 0} {processingTypeLabel}
				</Button>
			</div>
		</div>
	);

	const renderResultsTab = () => (
		<div style={{ ...TAB_BODY, display: "flex", flexDirection: "column", gap: 12 }}>
			{isCompleted && (
				<Text style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="results-step-completed">
					<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />
					Step completed · {results.length} result{results.length === 1 ? "" : "s"} uploaded
				</Text>
			)}
			{results.length > 0 ? (
				<DataTable
					bare
					columns={resultColumns}
					dataSource={results}
					rowKey="id"
					pagination={false}
					scrollX="max-content"
					expandable={{
						expandedRowRender: (record) => <UploadedResultSummary result={record} onRerun={loadExistingResults} />,
						expandedRowKeys: expandedResultIds,
						onExpandedRowsChange: (keys) => setExpandedResultIds([...keys]),
					}}
				/>
			) : (
				<EmptyState
					title="No results uploaded"
					description={
						isCompleted
							? "This step was completed without a result."
							: readOnly
							? "No result was uploaded for this step."
							: `Upload the first result to start processing these ${processingTypeLabel}.`
					}
					action={uploadClosed ? undefined : { label: "Go to upload", onClick: () => setActiveTab(UPLOAD_TAB) }}
				/>
			)}
		</div>
	);

	const resultsTab = {
		key: RESULTS_TAB,
		label: (
			<span>
				Results
				{results.length > 0 && (
					<span style={{ marginLeft: 5, fontSize: 10, color: "var(--mg-text-secondary)" }}>
						{results.length}
					</span>
				)}
			</span>
		),
		children: renderResultsTab(),
	};

	return (
		<Tabs
			size="small"
			activeKey={activeTab}
			onChange={setActiveTab}
			tabBarStyle={{ margin: 0, padding: "0 16px" }}
			items={uploadClosed ? [resultsTab] : [{ key: UPLOAD_TAB, label: "Upload", children: renderUploadTab() }, resultsTab]}
		/>
	);
};

export default ResultProcessingView;
